import { create } from 'zustand';
import {
  VenueDetailData,
  VenueFilterData,
  VenueIndexData,
  VenueTagData,
} from '../dto/EntityRepository';
import { VenueIndexView } from '../enum/EnumRepository';
import { getDefaultVenueFilterData } from '../util/venueListParams';
import {
  buildInterestExhibitionCards,
  InterestExhibitionCardData,
  mergeInterestExhibitionCards,
  removeInterestExhibitionCard,
  removeInterestExhibitionCardsByVenue,
} from '../util/interestExhibitions';

interface ExhibitionVenueState {
  venueList: VenueIndexData[];
  view: VenueIndexView;
  loadedView: VenueIndexView;
  selectedVenueId: string | null;
  displayedVenueId: string | null;
  selectedVenueIds: string[];
  interestExhibitions: InterestExhibitionCardData[];
  venueDetailCache: Record<string, VenueDetailData>;
  loadingVenueIds: string[];
  filterData: VenueFilterData;
  tags: VenueTagData[];
  setVenueList: (venueList: VenueIndexData[], view: VenueIndexView) => void;
  setView: (view: VenueIndexView) => void;
  selectVenueDetail: (venueId: string) => void;
  beginVenueDetailSelection: (venueId: string) => boolean;
  beginInterestVenueSelection: (venueId: string) => boolean;
  resolveVenueDetail: (venueId: string, venue: VenueDetailData) => void;
  rejectVenueDetail: (venueId: string) => void;
  clearVenueDetail: () => void;
  selectInterestVenue: (venueId: string) => void;
  addInterestExhibitions: (exhibitions: InterestExhibitionCardData[]) => void;
  removeInterestExhibition: (key: string) => void;
  removeInterestVenue: (venueId: string) => void;
  setVenueDetailCache: (venueId: string, venue: VenueDetailData) => void;
  setVenueLoading: (venueId: string, isLoading: boolean) => void;
  setFilterData: (filterData: VenueFilterData) => void;
  setTags: (tags: VenueTagData[]) => void;
}

export const useExhibitionVenueStore = create<ExhibitionVenueState>((set, get) => ({
  venueList: [],
  view: VenueIndexView.ALL_VENUES,
  loadedView: VenueIndexView.ALL_VENUES,
  selectedVenueId: null,
  displayedVenueId: null,
  selectedVenueIds: [],
  interestExhibitions: [],
  venueDetailCache: {},
  loadingVenueIds: [],
  filterData: getDefaultVenueFilterData(),
  tags: [],
  setVenueList: (venueList, loadedView) => set({ venueList, loadedView }),
  setView: (view) => set((state) => (state.view === view ? state : { view })),
  selectVenueDetail: (selectedVenueId) => set((state) => {
    const displayedVenueId = state.venueDetailCache[selectedVenueId] === undefined
      ? state.displayedVenueId
      : selectedVenueId;
    if (
      state.selectedVenueId === selectedVenueId
      && state.displayedVenueId === displayedVenueId
    ) return state;
    return { selectedVenueId, displayedVenueId };
  }),
  beginVenueDetailSelection: (venueId) => {
    const state = get();
    const shouldLoad = state.venueDetailCache[venueId] === undefined
      && !state.loadingVenueIds.includes(venueId);

    if (state.selectedVenueId !== venueId || shouldLoad) {
      set({
        selectedVenueId: venueId,
        displayedVenueId: state.venueDetailCache[venueId] === undefined
          ? state.displayedVenueId
          : venueId,
        loadingVenueIds: shouldLoad
          ? [...state.loadingVenueIds, venueId]
          : state.loadingVenueIds,
      });
    }

    return shouldLoad;
  },
  beginInterestVenueSelection: (venueId) => {
    const state = get();
    const cached = state.venueDetailCache[venueId];
    const shouldLoad = cached === undefined
      && !state.loadingVenueIds.includes(venueId);
    const isAlreadySelected = state.selectedVenueIds.includes(venueId);

    if (!isAlreadySelected || shouldLoad) {
      set({
        selectedVenueIds: isAlreadySelected
          ? state.selectedVenueIds
          : [...state.selectedVenueIds, venueId],
        loadingVenueIds: shouldLoad
          ? [...state.loadingVenueIds, venueId]
          : state.loadingVenueIds,
        interestExhibitions: cached === undefined
          ? state.interestExhibitions
          : mergeInterestExhibitionCards(
            state.interestExhibitions,
            buildInterestExhibitionCards(cached),
          ),
      });
    }

    return shouldLoad;
  },
  resolveVenueDetail: (venueId, venue) => set((state) => ({
    venueDetailCache: {
      ...state.venueDetailCache,
      [venueId]: venue,
    },
    displayedVenueId: state.selectedVenueId === venueId
      ? venueId
      : state.displayedVenueId,
    loadingVenueIds: state.loadingVenueIds.includes(venueId)
      ? state.loadingVenueIds.filter((id) => id !== venueId)
      : state.loadingVenueIds,
    interestExhibitions: state.selectedVenueIds.includes(venueId)
      ? mergeInterestExhibitionCards(
        state.interestExhibitions,
        buildInterestExhibitionCards(venue),
      )
      : state.interestExhibitions,
  })),
  rejectVenueDetail: (venueId) => set((state) => {
    const isInterestVenue = state.selectedVenueIds.includes(venueId);
    const isSelectedDetail = state.selectedVenueId === venueId;
    const isLoading = state.loadingVenueIds.includes(venueId);
    if (!isInterestVenue && !isSelectedDetail && !isLoading) return state;

    return {
      selectedVenueId: isSelectedDetail
        ? state.displayedVenueId
        : state.selectedVenueId,
      displayedVenueId: state.displayedVenueId,
      selectedVenueIds: isInterestVenue
        ? state.selectedVenueIds.filter((id) => id !== venueId)
        : state.selectedVenueIds,
      interestExhibitions: isInterestVenue
        ? removeInterestExhibitionCardsByVenue(state.interestExhibitions, venueId)
        : state.interestExhibitions,
      loadingVenueIds: isLoading
        ? state.loadingVenueIds.filter((id) => id !== venueId)
        : state.loadingVenueIds,
    };
  }),
  clearVenueDetail: () => set((state) => (
    state.selectedVenueId == null && state.displayedVenueId == null
      ? state
      : { selectedVenueId: null, displayedVenueId: null }
  )),
  selectInterestVenue: (venueId) => set((state) => (
    state.selectedVenueIds.includes(venueId)
      ? state
      : { selectedVenueIds: [...state.selectedVenueIds, venueId] }
  )),
  addInterestExhibitions: (exhibitions) => set((state) => ({
    interestExhibitions: mergeInterestExhibitionCards(
      state.interestExhibitions,
      exhibitions,
    ),
  })),
  removeInterestExhibition: (key) => set((state) => ({
    interestExhibitions: removeInterestExhibitionCard(
      state.interestExhibitions,
      key,
    ),
  })),
  removeInterestVenue: (venueId) => set((state) => {
    if (!state.selectedVenueIds.includes(venueId)) return state;
    return {
      selectedVenueIds: state.selectedVenueIds.filter((id) => id !== venueId),
      interestExhibitions: removeInterestExhibitionCardsByVenue(
        state.interestExhibitions,
        venueId,
      ),
      loadingVenueIds: state.selectedVenueId === venueId
        ? state.loadingVenueIds
        : state.loadingVenueIds.filter((id) => id !== venueId),
    };
  }),
  setVenueDetailCache: (venueId, venue) => set((state) => ({
    venueDetailCache: {
      ...state.venueDetailCache,
      [venueId]: venue,
    },
  })),
  setVenueLoading: (venueId, isLoading) => set((state) => {
    const hasVenue = state.loadingVenueIds.includes(venueId);
    if (hasVenue === isLoading) return state;
    return {
      loadingVenueIds: isLoading
        ? [...state.loadingVenueIds, venueId]
        : state.loadingVenueIds.filter((id) => id !== venueId),
    };
  }),
  setFilterData: (filterData) => set({ filterData }),
  setTags: (tags) => set({ tags }),
}));
